import type {
  OpportunityBoardFilters,
  OpportunityPeopleFilterCounts,
} from "@forgepoint/shared";
import {
  FilterBar,
  type FilterDefinition,
} from "@/components/filters/FilterBar";
import { customFieldFilterDefinition } from "@/components/filters/customFieldFilterDefinition";
import { idSelectionSummary } from "@/components/filters/idSelectionSummary";
import {
  customFieldId,
  parseCustomFieldId,
  withCustomFieldSelection,
  type FilterableCustomField,
} from "@/lib/customFieldFilters";
import { getUserDisplayName, useTeamsStore } from "@/store/teamsStore";
import { useCompaniesStore } from "@/modules/companies/companiesStore";
import { useLocationsStore } from "@/modules/locations/locationsStore";
import { OpportunityPersonFilterContent } from "./OpportunityPersonFilterContent";
import { OpportunityArchiveFilterContent } from "./OpportunityArchiveFilterContent";
import { OpportunityCompanyFilterContent } from "./OpportunityCompanyFilterContent";
import { OpportunityCreatedFilterContent } from "./OpportunityCreatedFilterContent";
import { OpportunityLocationFilterContent } from "./OpportunityLocationFilterContent";
import { createdRangeSummary } from "./createdRange";
import {
  ARCHIVE_MODE_OPTIONS,
  OPPORTUNITY_FILTER_TYPES,
  isPersonFilterKey,
  type OpportunityArchiveMode,
  type OpportunityCreatedRange,
  type OpportunityFilterKey,
} from "./opportunityFilterTypes";

interface OpportunityFilterBarProps {
  value: OpportunityBoardFilters;
  onChange: (value: OpportunityBoardFilters) => void;
  filterCounts: OpportunityPeopleFilterCounts | null;
  archiveMode: OpportunityArchiveMode;
  onArchiveModeChange: (mode: OpportunityArchiveMode) => void;
  createdRange: OpportunityCreatedRange | null;
  onCreatedRangeChange: (range: OpportunityCreatedRange | null) => void;
  onClearAll: () => void;
  showLocationFilter: boolean;
  customFields: FilterableCustomField[];
}

/**
 * The opportunity board's filter bar. Wraps the generic {@link FilterBar} with
 * the board's filter types (the three person roles, Company, Location, Created,
 * Archive, and one per filterable custom field), supplying each one's value
 * summary and editing dropdown.
 * @param value - The active person, company, location, and custom field selections
 * @param onChange - Called with the next person, company, location, and custom field selections
 * @param filterCounts - Per-user opportunity counts for the person dropdowns
 * @param archiveMode - The current archive view (Active / Archived / All)
 * @param onArchiveModeChange - Called with the next archive view
 * @param createdRange - The current created-date selection, or null when off
 * @param onCreatedRangeChange - Called with the next created-date selection
 * @param onClearAll - Called to reset every selection at once
 * @param showLocationFilter - Whether to offer the Location filter
 * @param customFields - The custom fields to offer as filters
 * @returns The opportunity filter bar
 */
export function OpportunityFilterBar({
  value,
  onChange,
  filterCounts,
  archiveMode,
  onArchiveModeChange,
  createdRange,
  onCreatedRangeChange,
  onClearAll,
  showLocationFilter,
  customFields,
}: OpportunityFilterBarProps) {
  const users = useTeamsStore((state) => state.users);
  const companies = useCompaniesStore((state) => state.companies);
  const locations = useLocationsStore((state) => state.locations);

  const userName = (id: string): string | null => {
    const user = users.find((candidate) => candidate.id === id);
    return user ? getUserDisplayName(user) : null;
  };

  const summaryFor = (key: OpportunityFilterKey): string | null => {
    if (isPersonFilterKey(key)) {
      return idSelectionSummary(value[key] ?? [], userName);
    }
    if (key === "companyIds") {
      return idSelectionSummary(
        value.companyIds ?? [],
        (id) => companies.get(id)?.name ?? null,
      );
    }
    if (key === "locationIds") {
      return idSelectionSummary(
        value.locationIds ?? [],
        (id) => locations.get(id)?.name ?? null,
      );
    }
    if (key === "created") {
      return createdRangeSummary(createdRange);
    }
    if (archiveMode === "active") {
      return null;
    }
    return (
      ARCHIVE_MODE_OPTIONS.find((option) => option.value === archiveMode)
        ?.label ?? archiveMode
    );
  };

  const contentFor = (key: OpportunityFilterKey) => {
    if (isPersonFilterKey(key)) {
      return (
        <OpportunityPersonFilterContent
          selected={value[key] ?? []}
          counts={filterCounts?.[key]}
          onChange={(next) =>
            onChange({ ...value, [key]: next.length ? next : undefined })
          }
        />
      );
    }
    if (key === "companyIds") {
      return (
        <OpportunityCompanyFilterContent
          selected={value.companyIds ?? []}
          onChange={(next) =>
            onChange({ ...value, companyIds: next.length ? next : undefined })
          }
        />
      );
    }
    if (key === "locationIds") {
      return (
        <OpportunityLocationFilterContent
          selected={value.locationIds ?? []}
          onChange={(next) =>
            onChange({ ...value, locationIds: next.length ? next : undefined })
          }
        />
      );
    }
    if (key === "created") {
      return (
        <OpportunityCreatedFilterContent
          value={createdRange}
          onChange={onCreatedRangeChange}
        />
      );
    }
    return (
      <OpportunityArchiveFilterContent
        value={archiveMode}
        onChange={onArchiveModeChange}
      />
    );
  };

  const customFieldFilterFor = (
    field: FilterableCustomField,
  ): FilterDefinition =>
    customFieldFilterDefinition({
      field,
      selected: value.customFields?.[field.name] ?? [],
      userName,
      onChange: (next) => setCustomFieldSelection(field.name, next),
    });

  const setCustomFieldSelection = (fieldName: string, next: string[]) => {
    onChange({
      ...value,
      customFields: withCustomFieldSelection(
        value.customFields,
        fieldName,
        next,
      ),
    });
  };

  const filters: FilterDefinition[] = [
    ...OPPORTUNITY_FILTER_TYPES.filter(
      (type) => showLocationFilter || type.key !== "locationIds",
    ).map((type) => ({
      key: type.key,
      label: type.label,
      icon: type.icon,
      summary: summaryFor(type.key),
      content: contentFor(type.key),
    })),
    ...customFields.map(customFieldFilterFor),
  ];

  const removeFilter = (key: string) => {
    const fieldName = parseCustomFieldId(key);
    if (fieldName !== null) {
      setCustomFieldSelection(fieldName, []);
      return;
    }
    if (!isOpportunityFilterKey(key)) {
      return;
    }
    if (
      isPersonFilterKey(key) ||
      key === "companyIds" ||
      key === "locationIds"
    ) {
      onChange({ ...value, [key]: undefined });
      return;
    }
    if (key === "created") {
      onCreatedRangeChange(null);
      return;
    }
    onArchiveModeChange("active");
  };

  return (
    <FilterBar
      filters={filters}
      initialActiveKeys={initialActiveKeys(value, archiveMode, createdRange)}
      onRemove={removeFilter}
      onClearAll={onClearAll}
    />
  );
}

/**
 * Seeds the visible chips from the initial filter state so any pre-applied
 * filters or non-default archive view show as chips on first render.
 * @param value - The active person, company, location, and custom field selections
 * @param archiveMode - The current archive view
 * @param createdRange - The current created-date selection
 * @returns The initial ordered list of active filter keys
 */
function initialActiveKeys(
  value: OpportunityBoardFilters,
  archiveMode: OpportunityArchiveMode,
  createdRange: OpportunityCreatedRange | null,
): OpportunityFilterKey[] {
  const order: OpportunityFilterKey[] = OPPORTUNITY_FILTER_TYPES.filter(
    (type) =>
      (isPersonFilterKey(type.key) ||
        type.key === "companyIds" ||
        type.key === "locationIds") &&
      (value[type.key]?.length ?? 0) > 0,
  ).map((type) => type.key);
  order.push(...Object.keys(value.customFields ?? {}).map(customFieldId));
  if (createdRange) {
    order.push("created");
  }
  if (archiveMode !== "active") {
    order.push("archive");
  }
  return order;
}

/**
 * Narrows a generic filter key from {@link FilterBar} to an opportunity one.
 * @param key - The key to test
 * @returns True when the key is an opportunity filter key
 */
function isOpportunityFilterKey(key: string): key is OpportunityFilterKey {
  return OPPORTUNITY_FILTER_TYPES.some((type) => type.key === key);
}
